import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import authService from '@/features/auth/services/authService';
import { AUTH_CONFIG } from '@/features/auth/config/authConfig';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

import logger from "../utils/secureLogger";

function AuthCallback() {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuthStore();

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const handleAuthCallback = async () => {
    try {
      setStatus('processing');
      logger.debug('🔄 Processing Okta authentication callback...');
      
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const userData = await authService.getCompleteUserData();
      
      if (!userData) {
        throw new Error('No user data received from authentication');
      }

      login(userData);
      
      logger.debug('✅ Okta Authentication successful');
      logger.debug('👤 Username:', userData.username);
      logger.debug('📧 Email:', userData.email);
      logger.debug('👥 User groups:', userData.groups);
      
      setStatus('success');
      
      setTimeout(() => {
        navigate(AUTH_CONFIG.ROUTES.DEFAULT_REDIRECT, { replace: true });
      }, 1500);
      
    } catch (error) {
      logger.error('❌ Auth callback error:', error);
      
      try {
        logger.debug('🔄 Trying alternative token extraction...');
        const alternativeUserData = await extractUserDataFromLocalStorage();
        
        if (alternativeUserData) {
          login(alternativeUserData);
          logger.debug('✅ Alternative authentication successful');
          setStatus('success');
          
          setTimeout(() => {
            navigate(AUTH_CONFIG.ROUTES.DEFAULT_REDIRECT, { replace: true });
          }, 1500);
          return;
        }
      } catch (altError) {
        logger.debug('❌ Alternative approach also failed:', altError);
      }
      
      setError(error.message || 'Authentication failed');
      setStatus('error');
      
      setTimeout(() => {
        navigate('/login?error=auth_failed', { replace: true });
      }, 3000);
    }
  };

  const extractUserDataFromLocalStorage = async () => {
    try {
      const keys = Object.keys(localStorage);
      const idTokenKey = keys.find(key => key.includes('idToken'));
      
      if (!idTokenKey) {
        throw new Error('No ID token found in localStorage');
      }
      
      const idToken = localStorage.getItem(idTokenKey);
      if (!idToken) {
        throw new Error('ID token is empty');
      }
      
      const tokenPayload = JSON.parse(atob(idToken.split('.')[1]));
      logger.debug('📋 Token payload:', tokenPayload);
      
      const userData = {
        username: tokenPayload.email || tokenPayload['cognito:username'] || tokenPayload.sub,
        email: tokenPayload.email,
        attributes: {
          email: tokenPayload.email,
          given_name: tokenPayload.given_name,
          family_name: tokenPayload.family_name,
          name: tokenPayload.name,
          'custom:Group': tokenPayload['custom:Group']
        },
        groups: tokenPayload['cognito:groups'] || [AUTH_CONFIG.USER_GROUPS.DEFAULT],
        sub: tokenPayload.sub
      };
      
      logger.debug('✅ Extracted user data from localStorage:', userData);
      return userData;
      
    } catch (error) {
      logger.error('❌ Error extracting from localStorage:', error);
      throw error;
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <>
            <Loader2 className="w-12 h-12 text-[#F26522] animate-spin mx-auto mb-6" />
            <h2 className="text-xl font-bold text-white mb-3">Processing Authentication...</h2>
            <p className="text-gray-400">Please wait while we complete your login</p>
          </>
        );
      
      case 'success':
        return (
          <>
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-white mb-3">Authentication Successful!</h2>
            <p className="text-gray-400">Redirecting to CES Portal...</p>
          </>
        );
      
      case 'error':
        return (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-white mb-3">Authentication Failed</h2>
            <p className="text-red-400 text-sm mb-4 bg-red-900/30 border border-red-800 p-3 rounded-lg">{error}</p>
            <p className="text-gray-400">Redirecting to login page...</p>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-900 to-[#F26522]/20 p-4">
      <div className="bg-gray-900 rounded-2xl shadow-2xl p-12 text-center max-w-md w-full relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[#F26522]/20 pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-[#F26522]/15 pointer-events-none" />
        <div className="relative">
          <img 
            src="/LogoLiberty.png" 
            alt="Liberty Latin America" 
            className="h-16 mx-auto mb-8 brightness-0 invert" 
          />
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default AuthCallback;
